import React, { useCallback, useEffect, useRef, useState } from 'react';

import './styles.css';

interface AnimatedTextProps {
  fullText: string;
  delay?: number;
  interval?: number;
  showUnderline?: boolean;
  onEnd?: () => void;
}

const AnimatedText = ({
  fullText,
  interval = 100,
  showUnderline = false,
  delay = 0,
  onEnd
}: AnimatedTextProps) => {
  const intervalIdRef = useRef<NodeJS.Timeout>();
  const animationCompletedRef = useRef(false);

  const [text, setText] = useState('');

  const addDelay = useCallback(async () => {
    await new Promise((resolve) => setTimeout(resolve, delay));
  }, [delay]);

  const renderFormattedText = (text: string) => {
    return text.split(/(&b&.*?&b&)/).map((part, index) => {
      if (part.startsWith('&b&') && part.endsWith('&b&')) {
        return <b key={index}>{part.slice(3, -3)}</b>;
      }

      return part;
    });
  };

  const startAnimation = useCallback(async () => {
    if (animationCompletedRef.current) {
      setText(fullText);

      return;
    }

    await addDelay();
    let i = 0;
    const intervalId = setInterval(() => {
      const currentText = fullText.slice(0, i);
      const lastOpenTagIndex = currentText.lastIndexOf('&b&');
      const lastCloseTagIndex = currentText.lastIndexOf(
        '&b&',
        lastOpenTagIndex - 1
      );

      if (lastOpenTagIndex > lastCloseTagIndex && lastCloseTagIndex !== -1) {
        setText(currentText.slice(0, lastOpenTagIndex));
      } else {
        setText(currentText);
      }

      i++;
      if (i > fullText.length) {
        clearInterval(intervalId);
        animationCompletedRef.current = true;
        onEnd?.();
      }
    }, interval);

    intervalIdRef.current = intervalId;
  }, [addDelay, interval, fullText, onEnd]);

  useEffect(() => {
    startAnimation();

    return () => {
      clearInterval(intervalIdRef.current);
    };
  }, [startAnimation]);

  return (
    <span>
      {renderFormattedText(text)}
      {showUnderline ? <span className="animate-pulse">|</span> : null}
    </span>
  );
};

interface JumpingDotsProps {
  size?: string;
  color?: string;
}

const JumpingDots: React.FC<JumpingDotsProps> = ({
  size = 'size-2.5',
  color = '#E0E0E0'
}) => {
  return (
    <div className="flex space-x-1 animated-points">
      <span
        className={`${size} rounded-full point`}
        style={{ backgroundColor: color }}
      />
      <span
        className={`${size} rounded-full point`}
        style={{ backgroundColor: color }}
      />
      <span
        className={`${size} rounded-full point`}
        style={{ backgroundColor: color }}
      />
    </div>
  );
};

const LMLoading: React.FC<{
  small: boolean;
  color?: string;
  messageOnLongWait?: boolean;
}> = ({ small = false, color = '#E0E0E0', messageOnLongWait = false }) => {
  const [longWaitMessage, setLongWaitMessage] = useState<string | null>(null);
  const [messageAppeared, setMessageAppeared] = useState(false);

  useEffect(() => {
    if (messageOnLongWait) {
      const timer = setTimeout(() => {
        setMessageAppeared(false);
        setLongWaitMessage('Working on it');
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [messageOnLongWait]);

  const dotSize = small ? 'size-1' : 'size-2.5';

  return (
    <>
      {longWaitMessage && (
        <h4 className="text-md text-black font-500 leading-[32px] whitespace-pre-line flex flex-row items-center">
          <AnimatedText
            fullText={longWaitMessage}
            interval={30}
            onEnd={() => setMessageAppeared(true)}
          />
          {messageAppeared && (
            <div className="flex items-center h-full space-x-2 ml-2">
              <JumpingDots size="size-2" color={color} />
            </div>
          )}
        </h4>
      )}
      {!longWaitMessage && (
        <div className="flex items-center h-full space-x-2 mt-2.5">
          <JumpingDots size={dotSize} color={color} />
        </div>
      )}
    </>
  );
};

export default LMLoading;
